import React, { useState, useRef, useEffect } from 'react';
import { AutoComplete, Input, Spin } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { FormLabel } from '@/components/Form';
import ErrorText from '../ErrorText';

interface GoogleCustomLocationFieldProps {
  name: string;
  control?: any;
  placeholder?: string;
  error?: string;
  label?: string;
  size?: 'large' | 'middle';
  required?: boolean;
  disabled?: boolean;
  location?: 'australia' | 'overseas';
  setPropertyDetailsObject?: (details: any) => void;
  /**
   * Fires with the chosen prediction when the user picks one out of the
   * dropdown, and with `null` as soon as they type again. Lets a form require
   * that the stored address actually came from Google rather than free text.
   */
  onSelectedAddressChange?: (address: string | null) => void;
}

const GoogleCustomLocationField: React.FC<GoogleCustomLocationFieldProps> = ({
  name,
  control,
  placeholder,
  error,
  label,
  size = 'middle',
  required = false,
  disabled = false,
  location = 'australia',
  setPropertyDetailsObject,
  onSelectedAddressChange,
}) => {
  const [options, setOptions] = useState<{ value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput] = useDebounce(inputValue, 400);

  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null,
  );
  const mapRef = useRef<HTMLDivElement | null>(null); // dummy div for PlacesService
  const lastSelectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!window.google || !window.google.maps) return;

    // Initialize autocomplete service
    if (!autocompleteService.current) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }

    // Initialize places service
    if (mapRef.current && !placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        mapRef.current,
      );
    }

    if (debouncedInput.length < 4) {
      setOptions([]);
      return;
    }

    setIsLoading(true);
    autocompleteService.current.getPlacePredictions(
      {
        input: debouncedInput,
        types: ['address'],
        ...(location === 'australia' && {
          componentRestrictions: { country: 'au' },
        }),
      },
      (predictions) => {
        setIsLoading(false);
        if (predictions) {
          const newOptions = predictions.map((p) => ({
            value: p.description,
          }));
          setOptions(newOptions);
        } else {
          setOptions([]);
        }
      },
    );
  }, [debouncedInput]);

  const getPropertyDetails = (placeId: string) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address'],
      },
      (placeResult, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          placeResult
        ) {
          const components = placeResult.address_components || [];

          const getComponent = (type: string) =>
            components.find((c) => c.types.includes(type))?.long_name || '';

          const apartment = getComponent('subpremise');
          const streetNumber = getComponent('street_number');
          const street = getComponent('route');
          const suburb = getComponent('locality');
          const state = getComponent('administrative_area_level_1');
          const postcode = getComponent('postal_code');

          if (setPropertyDetailsObject) {
            setPropertyDetailsObject({
              apartment,
              streetNumber,
              street,
              suburb,
              state,
              postcode,
              fullAddress: placeResult.formatted_address,
            });
          }
        }
      },
    );
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const handleChange = (value: string) => {
            setInputValue(value);
            field.onChange(value);
            // Editing away from the chosen prediction means the value is no
            // longer a confirmed Google result. Comparing against the last
            // selection keeps this safe if antd also fires onSearch on select.
            if (value !== lastSelectedRef.current) {
              onSelectedAddressChange?.(null);
            }
          };

          const handleSelect = (value: string) => {
            setInputValue(value);
            field.onChange(value);
            lastSelectedRef.current = value;
            onSelectedAddressChange?.(value);

            if (!autocompleteService.current) return;

            autocompleteService.current.getPlacePredictions(
              {
                input: value,
                types: ['address'],
                ...(location === 'australia' && {
                  componentRestrictions: { country: 'au' },
                }),
              },
              (predictions) => {
                if (predictions && predictions.length > 0) {
                  const selected = predictions.find(
                    (p) => p.description === value,
                  );
                  if (selected?.place_id) {
                    getPropertyDetails(selected.place_id);
                  }
                }
              },
            );
          };

          return (
            <>
              {label && <FormLabel required={required} label={label} />}
              <AutoComplete
                style={{ width: '100%' }}
                options={options}
                onSearch={handleChange}
                onSelect={handleSelect}
                value={field.value}
                size={size}
                disabled={disabled}
                status={error ? 'error' : undefined}
              >
                <Input
                  size={size}
                  placeholder={placeholder || 'Search address...'}
                  prefix={<EnvironmentOutlined />}
                  suffix={isLoading ? <Spin size="small" /> : null}
                  status={error ? 'error' : undefined}
                />
              </AutoComplete>
              {error && <ErrorText error={error} />}
            </>
          );
        }}
      />
      <div ref={mapRef} style={{ display: 'none' }} />
    </>
  );
};

export default GoogleCustomLocationField;
